/**
 * useWatchlist — client-only marketplace watchlist (web).
 *
 * A buyer can "star" listings to track them. This is a DEGRADED, client-side
 * feature: there is NO backend watchlist endpoint yet, so the starred set lives
 * in `localStorage` under {@link WATCH_KEY} and is reactive across every screen
 * via `useSyncExternalStore` + a tiny pub-sub (mirrors the per-device read-state
 * model in `notifications/readState.web.ts`). The set is an array of listing
 * ids (the `ListingDto.id`); resolving ids → listings is the caller's job
 * (the Watchlist screen filters the already-fetched browse listings by id).
 *
 * Design reference: `Documentation/Claude design files/.../buyer/BuyerShared.jsx`
 * (`getWatchlist`/`toggleWatch`/`useWatchlist`). When a server-backed watchlist
 * lands (TD — buyer watchlist persistence), this hook can be repointed to a
 * query/mutation pair and the localStorage store dropped.
 */
import { useSyncExternalStore } from 'react';

/** localStorage key for the starred-listing-id set (JSON string[]). */
const WATCH_KEY = 'fse.watchlist.v1';

/** Subscribers notified on every mutation so all live screens re-render. */
const subscribers = new Set<() => void>();

/**
 * Cached snapshot. `useSyncExternalStore` requires `getSnapshot` to return a
 * STABLE reference between renders when nothing changed (else it loops), so we
 * memoise the parsed array and only swap it on a real mutation / cross-tab
 * `storage` event.
 */
let snapshot: string[] = readFromStorage();

function readFromStorage(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(WATCH_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string');
  } catch {
    return [];
  }
}

function persist(next: string[]): void {
  snapshot = next;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(WATCH_KEY, JSON.stringify(next));
    } catch {
      /* private-mode / quota — the in-memory snapshot still drives this tab */
    }
  }
  for (const cb of subscribers) cb();
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  // Keep tabs in sync: another tab's write fires a `storage` event here.
  const onStorage = (e: StorageEvent) => {
    if (e.key === WATCH_KEY) {
      snapshot = readFromStorage();
      cb();
    }
  };
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    subscribers.delete(cb);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

function getSnapshot(): string[] {
  return snapshot;
}

/** Server snapshot (SSR / first paint) — always empty, this is client state. */
function getServerSnapshot(): string[] {
  return [];
}

/** Add or remove `id` from the watchlist. Returns true if now watched. */
export function toggleWatch(id: string): boolean {
  const current = snapshot;
  const isOn = current.includes(id);
  const next = isOn ? current.filter((x) => x !== id) : [...current, id];
  persist(next);
  return !isOn;
}

/** Imperative read of the current watched-id set (outside React). */
export function getWatchlistIds(): string[] {
  return snapshot;
}

export interface UseWatchlist {
  /** The set of watched listing ids (reactive). */
  ids: string[];
  /** True when `id` is currently watched. */
  isWatched: (id: string) => boolean;
  /** Toggle `id`; returns the new watched state. */
  toggle: (id: string) => boolean;
  /** Number of watched listings. */
  count: number;
}

/**
 * Reactive watchlist hook. Re-renders the consumer whenever the starred set
 * changes anywhere (this tab or another). `isWatched`/`toggle` are stable.
 */
export function useWatchlist(): UseWatchlist {
  const ids = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return {
    ids,
    isWatched: (id: string) => ids.includes(id),
    toggle: toggleWatch,
    count: ids.length,
  };
}
